import { forwardRef } from 'react';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import type { AreaBarChartAxis } from './AreaBarChart';
import type { AxisMatrix } from './computeAxisMatrix';
import { barFill, barFillBackgroundSize, POLARITY_DISPLAY_ORDER } from './barFillStyles';

/**
 * エリア選択集計 PDF 出力テンプレート (2026-07-16、`apps/user/src/features/insights/
 * ReportPdfTemplate.tsx` の隠しDOM方式を踏襲)。
 *
 * inline style の例外: 本リポジトリのデザイン規律では原則 inline style 禁止だが、
 * html2canvas が computed style を採取する都合上、PDF出力テンプレートでは inline style が
 * デファクト・スタンダード (`ReportPdfTemplate.tsx` と同じ理由)。
 *
 * 地図画像は `generateAreaReportPdf` が呼出直前に `img[alt="地図"]` の src を直接書き換える
 * (React state 経由だと再レンダリング待ちのタイミング制御が煩雑になるため、
 * `generateIdeaPdf.ts` と同じ「imperative に DOM を書き換える」方式を踏襲)。
 *
 * `.pdf-section` は `generateAreaReportPdf` が html2canvasで丸ごと1枚の画像として
 * キャプチャする単位 (ページをまたいで分割されない、page内に収まらなければ丸ごと次ページへ)。
 * 2026-07-16: 当初 6+地点数 個のsectionに分けていたところ、地点数が少ない (=ページ末尾の
 * 残り空きが少ない) 場合に、末尾のフッターだけが単独で次ページに弾かれ「1ページ+ほぼ空白の
 * 2ページ目」になる不具合が判明。section数を3つ (ヘッダー+地図 / AI要約+件数 /
 * 地点一覧+フッター) に統合し、フッターが単独で page break の原因にならないようにした。
 * 地点一覧セクションが1ページに収まらないほど長い場合は、`generateAreaReportPdf` の
 * 「1page超のジャンボsectionを分割」ロジックが複数ページにまたがせる。
 *
 * 件数の可視化は当初レーダーチャートだったが、特定の役割 (例:「移動・交通」) に検出数が
 * 大きく偏るとスパイク状になり読み取れなくなるため、横棒グラフ (`AreaBarChart`) に
 * 置き換えた (2026-07-16、ユーザー指摘起点)。
 */

export interface AreaReportPdfTemplateProps {
  pointCount: number;
  summary: string | null;
  axes: AreaBarChartAxis[];
  posts: StreetSurveyPost[];
  generatedAt: string;
  /** 画面で選んだ集計項目・細かさ・表示 (docs/27 §9-3、2026-09-16)。PDF も同じ内容にする。 */
  axisLabel?: string;
  granularityLabel?: string;
  view?: 'bar' | 'matrix';
  matrix?: AxisMatrix | null;
  matrixRowLabel?: string;
  matrixColLabel?: string;
}

export const AreaReportPdfTemplate = forwardRef<HTMLDivElement, AreaReportPdfTemplateProps>(
  function AreaReportPdfTemplate(
    {
      pointCount,
      summary,
      axes,
      posts,
      generatedAt,
      axisLabel,
      granularityLabel,
      view = 'bar',
      matrix = null,
      matrixRowLabel,
      matrixColLabel,
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        style={{
          width: '794px',
          background: '#ffffff',
          fontFamily:
            '"Helvetica Neue", Arial, "Hiragino Kaku Gothic ProN", "Hiragino Sans", Meiryo, sans-serif',
          color: '#333',
          lineHeight: 1.6,
          boxSizing: 'border-box',
          position: 'relative',
        }}
      >
        {/* 1. ヘッダー + 地図 */}
        <div
          className="pdf-section"
          style={{
            width: '794px',
            boxSizing: 'border-box',
            background: '#fff',
            padding: '32px 40px 0 40px',
          }}
        >
          <div
            style={{
              borderBottom: '2px solid #2c3e50',
              paddingBottom: '14px',
              marginBottom: '16px',
            }}
          >
            <h1 style={{ fontSize: '24px', margin: '0 0 6px 0', color: '#2c3e50' }}>
              エリア選択集計（{pointCount}地点）
            </h1>
            <div style={{ fontSize: '12px', color: '#666' }}>出力日時: {generatedAt}</div>
          </div>
          <h3
            style={{
              fontSize: '14px',
              color: '#2c3e50',
              marginBottom: '6px',
              borderBottom: '1px solid #ddd',
              paddingBottom: '4px',
            }}
          >
            選択エリアと地点
          </h3>
          <div
            style={{
              height: '240px',
              marginBottom: '20px',
              borderRadius: '8px',
              border: '1px solid #eee',
              background: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              alt="地図"
              loading="eager"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                display: 'block',
              }}
            />
          </div>
        </div>

        {/* 2. AI要約 + 調査項目ごとの件数の棒グラフ / マトリクス */}
        <div
          className="pdf-section"
          style={{
            width: '794px',
            boxSizing: 'border-box',
            background: '#fff',
            padding: '0 40px',
            marginBottom: '20px',
          }}
        >
          <h2
            style={{
              fontSize: '15px',
              color: '#2c3e50',
              borderLeft: '5px solid #0EA5E9',
              paddingLeft: '10px',
              marginBottom: '8px',
            }}
          >
            エリアの特徴
          </h2>
          <div
            style={{
              background: '#f9fafb',
              padding: '12px 14px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              whiteSpace: 'pre-wrap',
              fontSize: '13px',
              marginBottom: '16px',
            }}
          >
            {summary || '(要約は生成されていません)'}
          </div>
          <h3 style={{ fontSize: '14px', color: '#2c3e50', marginBottom: '6px' }}>
            {view === 'matrix' && matrix
              ? `${matrixRowLabel ?? '調査項目'} × ${matrixColLabel ?? '調査項目'} の検出要素数`
              : `${axisLabel ?? '調査項目'}ごとの検出要素数`}
            {granularityLabel ? `（${granularityLabel}）` : ''}
          </h3>
          {view === 'matrix' && matrix && matrix.rows.length > 0 && matrix.cols.length > 0 ? (
            // html2canvas は flexbox の gap 等を正しく描けないため、幅を明示した素の table にする。
            <table
              style={{
                width: '714px',
                borderCollapse: 'collapse',
                fontSize: '11px',
                marginBottom: '8px',
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      border: '1px solid #e2e8f0',
                      background: '#f8fafc',
                      padding: '4px 6px',
                      textAlign: 'left',
                    }}
                  >
                    {matrixRowLabel ?? ''} ＼ {matrixColLabel ?? ''}
                  </th>
                  {matrix.cols.map((c) => (
                    <th
                      key={c.key}
                      style={{
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc',
                        padding: '4px 6px',
                        textAlign: 'center',
                      }}
                    >
                      {c.label}
                    </th>
                  ))}
                  <th
                    style={{
                      border: '1px solid #e2e8f0',
                      background: '#f8fafc',
                      padding: '4px 6px',
                      textAlign: 'center',
                    }}
                  >
                    計
                  </th>
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map((r, ri) => (
                  <tr key={r.key}>
                    <th
                      style={{
                        border: '1px solid #e2e8f0',
                        padding: '4px 6px',
                        textAlign: 'left',
                        fontWeight: 600,
                      }}
                    >
                      {r.label}
                    </th>
                    {matrix.cols.map((c, ci) => {
                      const count = matrix.counts[ri]?.[ci] ?? 0;
                      return (
                        <td
                          key={c.key}
                          style={{
                            border: '1px solid #e2e8f0',
                            padding: '4px 6px',
                            textAlign: 'center',
                            background: count > 0 ? `${c.color}22` : '#ffffff',
                          }}
                        >
                          {count}
                        </td>
                      );
                    })}
                    <td
                      style={{
                        border: '1px solid #e2e8f0',
                        padding: '4px 6px',
                        textAlign: 'center',
                        fontWeight: 700,
                      }}
                    >
                      {matrix.rowTotals[ri] ?? 0}
                    </td>
                  </tr>
                ))}
                <tr>
                  <th
                    style={{
                      border: '1px solid #e2e8f0',
                      background: '#f8fafc',
                      padding: '4px 6px',
                      textAlign: 'left',
                    }}
                  >
                    計
                  </th>
                  {matrix.cols.map((c, ci) => (
                    <td
                      key={c.key}
                      style={{
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc',
                        padding: '4px 6px',
                        textAlign: 'center',
                        fontWeight: 700,
                      }}
                    >
                      {matrix.colTotals[ci] ?? 0}
                    </td>
                  ))}
                  <td
                    style={{
                      border: '1px solid #e2e8f0',
                      background: '#f8fafc',
                      padding: '4px 6px',
                      textAlign: 'center',
                      fontWeight: 700,
                    }}
                  >
                    {matrix.total}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : null}
          {/* 棒グラフ表示のときは従来どおり横棒を描く (マトリクス表示のときは上の表のみ)。 */}
          {/* html2canvas は flexbox の gap / text-overflow:ellipsis、inline-block +
              vertical-align:middle での中央揃え、% 幅のバー塗りつぶし、overflow:hidden を伴う
              テキストのゴースト表示など、正確に再現できない既知の問題が何重にも見つかった
              (2026-07-16、実運用で発覚)。加えて、行を position:absolute な子要素だけで構成すると
              コンテナ自身の高さが html2canvas に正しく測定されず末尾の行が見切れる不具合も判明した
              ため、コンテナの高さも行数から明示的に計算して指定する (auto計算に一切頼らない)。 */}
          {view === 'matrix' && matrix
            ? null
            : (() => {
                const ROW_H = 16;
                const ROW_GAP = 6;
                const DOT_SIZE = 9;
                const BAR_H = 14;
                const LABEL_X = 15;
                const LABEL_W = 145;
                const BAR_X = LABEL_X + LABEL_W; // 160
                const BAR_W = 518;
                const VALUE_X = BAR_X + BAR_W + 8; // 686
                const VALUE_W = 28;
                const maxValue = Math.max(1, ...axes.map((x) => x.value));
                const rows = [...axes]
                  .sort((a, b) => b.value - a.value)
                  .map((a) => {
                    const barFillPx = Math.round((a.value / maxValue) * BAR_W);
                    return (
                      <div
                        key={a.key}
                        style={{
                          position: 'relative',
                          height: `${ROW_H}px`,
                          fontSize: '12px',
                          marginBottom: `${ROW_GAP}px`,
                        }}
                      >
                        <span
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: `${(ROW_H - DOT_SIZE) / 2}px`,
                            width: `${DOT_SIZE}px`,
                            height: `${DOT_SIZE}px`,
                            borderRadius: '50%',
                            ...barFill(a.color, a.polarityKind ?? null),
                          }}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            left: `${LABEL_X}px`,
                            top: 0,
                            width: `${LABEL_W}px`,
                            lineHeight: `${ROW_H}px`,
                          }}
                        >
                          {a.label}
                        </span>
                        <span
                          style={{
                            position: 'absolute',
                            left: `${BAR_X}px`,
                            top: `${(ROW_H - BAR_H) / 2}px`,
                            width: `${BAR_W}px`,
                            height: `${BAR_H}px`,
                            borderRadius: '4px',
                            background: '#f1f5f9',
                            overflow: 'hidden',
                          }}
                        >
                          {/* 評価の内訳があるときは、画面と同じく区分ごとに積み上げる
                              (区切りは白い線。html2canvas が模様を描けない場合も濃淡で区別できる)。 */}
                          {a.polarity && a.value > 0 ? (
                            <span
                              style={{
                                display: 'block',
                                height: '100%',
                                width: `${barFillPx}px`,
                                borderRadius: '4px',
                                overflow: 'hidden',
                                position: 'relative',
                              }}
                            >
                              {(() => {
                                let left = 0;
                                const parts: JSX.Element[] = [];
                                for (const k of POLARITY_DISPLAY_ORDER) {
                                  const n = a.polarity![k];
                                  if (n <= 0) continue;
                                  const w = Math.round((n / a.value) * barFillPx);
                                  parts.push(
                                    <span
                                      key={k}
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: `${left}px`,
                                        width: `${w}px`,
                                        height: '100%',
                                        borderRight: '1px solid #ffffff',
                                        ...barFill(a.color, k),
                                        backgroundSize: barFillBackgroundSize(k),
                                      }}
                                    />,
                                  );
                                  left += w;
                                }
                                return parts;
                              })()}
                            </span>
                          ) : (
                            <span
                              style={{
                                display: 'block',
                                height: '100%',
                                borderRadius: '4px',
                                width: `${barFillPx}px`,
                                // html2canvas が模様を描けない場合も backgroundColor
                                // (薄いベタ塗り) にフォールバックする。
                                ...barFill(a.color, a.polarityKind ?? null),
                                backgroundSize: barFillBackgroundSize(a.polarityKind ?? null),
                              }}
                            />
                          )}
                        </span>
                        <strong
                          style={{
                            position: 'absolute',
                            left: `${VALUE_X}px`,
                            top: 0,
                            width: `${VALUE_W}px`,
                            height: `${ROW_H}px`,
                            lineHeight: `${ROW_H}px`,
                            textAlign: 'right',
                          }}
                        >
                          {a.value}
                        </strong>
                      </div>
                    );
                  });
                return (
                  <div
                    style={{
                      position: 'relative',
                      height: `${axes.length * (ROW_H + ROW_GAP)}px`,
                    }}
                  >
                    {rows}
                  </div>
                );
              })()}
        </div>

        {/* 3. 地点一覧 + フッター (1つのsectionにまとめ、フッター単独でのpage break を防ぐ)。 */}
        <div
          className="pdf-section"
          style={{
            width: '794px',
            boxSizing: 'border-box',
            background: '#fff',
            padding: '0 40px 32px 40px',
          }}
        >
          <h2
            style={{
              fontSize: '15px',
              color: '#2c3e50',
              borderLeft: '5px solid #0EA5E9',
              paddingLeft: '10px',
              marginBottom: '8px',
            }}
          >
            解析対象となった地点一覧
          </h2>
          {posts.map((p, i) => (
            <div
              key={p.id}
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                padding: '8px 12px',
                fontSize: '12px',
                display: 'flex',
                marginBottom: '6px',
              }}
            >
              <span style={{ color: '#94a3b8', marginRight: '10px', flexShrink: 0 }}>{i + 1}.</span>
              <span style={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                {p.description || '(説明なし)'}
              </span>
            </div>
          ))}
          <div
            style={{
              textAlign: 'center',
              paddingTop: '14px',
              marginTop: '10px',
              borderTop: '1px solid #eee',
              color: '#999',
              fontSize: '11px',
            }}
          >
            CitizenWalk — ストリートビュー机上調査 エリア選択集計
          </div>
        </div>
      </div>
    );
  },
);
